import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Stack,
    Collapse,
    IconButton,
    Card,
    CardContent,
    Divider,
    Chip,
    FormHelperText
} from '@mui/material';
import { Add, ExpandMore, ExpandLess, Delete, ShoppingCart } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';

const ApparelOrderForm = ({ onSubmitSuccess, defaultOpen = false, compact = false }) => {
    const { user } = useAuth();
    const [formOpen, setFormOpen] = useState(Boolean(defaultOpen));
    React.useEffect(() => {
        if (compact) {
            setFormOpen(true);
        }
    }, [compact]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [items, setItems] = useState([{
        shirtTypeItemNumber: '',
        shirtColor: '',
        letteringColor: 'Black',
        size: 'M',
        sizeType: 'Regular',
        quantity: 1,
        notes: ''
    }]);
    
    // Shipping address and order details removed per requirements

    const itemTypes = [
        { value: 'shirt', label: 'T-Shirt' },
        { value: 'polo', label: 'Polo Shirt' },
        { value: 'hoodie', label: 'Hoodie' },
        { value: 'jacket', label: 'Jacket' },
        { value: 'vest', label: 'Safety Vest' },
        { value: 'hat', label: 'Hat/Cap' },
        { value: 'pants', label: 'Pants' },
        { value: 'other', label: 'Other' }
    ];

    const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
    
    const urgencyLevels = [
        { value: 'standard', label: 'Standard (2-3 weeks)' },
        { value: 'rush', label: 'Rush (1-2 weeks)' },
        { value: 'urgent', label: 'Urgent (3-5 days)' }
    ];

    const addItem = () => {
        setItems([...items, {
            shirtTypeItemNumber: '',
            shirtColor: '',
            letteringColor: 'Black',
            size: 'M',
            sizeType: 'Regular',
            quantity: 1,
            notes: ''
        }]);
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    const updateItem = (index, field, value) => {
        const updatedItems = items.map((item, i) => 
            i === index ? { ...item, [field]: value } : item
        );
        setItems(updatedItems);
        
        if (error) setError('');
    };

    // Removed handlers for shipping address and order details

    const validateForm = () => {
        // Validate items
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item.shirtTypeItemNumber.trim()) {
                setError(`Shirt Type & Item # is required for item ${i + 1}`);
                return false;
            }
            if (!item.shirtColor.trim()) {
                setError(`Shirt Color is required for item ${i + 1}`);
                return false;
            }
            if (item.quantity < 1 || item.quantity > 10) {
                setError(`Quantity for item ${i + 1} must be between 1 and 10`);
                return false;
            }
        }
        
        // No shipping address required
        return true;
    };

    const calculateTotalItems = () => {
        return items.reduce((total, item) => total + item.quantity, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setLoading(true);
        setError('');
        
        try {
            const response = await apiClient.post('/forms/apparel', {
                items
            });
            
            setSuccess('Apparel order submitted successfully!');
            
            // Reset form
            setItems([{
                shirtTypeItemNumber: '',
                shirtColor: '',
                letteringColor: 'Black',
                size: 'M',
                sizeType: 'Regular',
                quantity: 1,
                notes: ''
            }]);
            // No-op: shipping address and order details removed
            
            // Close form after short delay
            setTimeout(() => {
                setFormOpen(false);
                setSuccess('');
                if (onSubmitSuccess) onSubmitSuccess(response?.data?.data);
            }, 2000);
            
        } catch (error) {
            console.error('Error submitting apparel order:', error);
            setError(error.response?.data?.message || 'Failed to submit order');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper sx={{ p: compact ? 2 : 3, mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={compact ? 1 : 2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography
                        variant={compact ? 'h5' : 'h6'}
                        sx={compact ? { fontWeight: 800, color: '#E94F37', lineHeight: 1.15, borderLeft: '4px solid #E94F37', pl: 1.25 } : undefined}
                    >
                        Submit Apparel Order
                    </Typography>
                    {items.length > 0 && (
                        <Chip 
                            label={`${calculateTotalItems()} items`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                        />
                    )}
                </Stack>
                {!compact && (
                    <IconButton onClick={() => setFormOpen(!formOpen)}>
                        {formOpen ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                )}
            </Stack>
            
            <Collapse in={formOpen}>
                <Box component="form" onSubmit={handleSubmit}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {/* Items Section */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Items to Order
                    </Typography>
                    
                    {items.map((item, index) => (
                        <Card key={index} sx={{ mb: 2 }}>
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Item {index + 1}
                                    </Typography>
                                    {items.length > 1 && (
                                        <IconButton 
                                            size="small" 
                                            color="error"
                                            onClick={() => removeItem(index)}
                                        >
                                            <Delete />
                                        </IconButton>
                                    )}
                                </Stack>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Shirt Type & Item #"
                                            value={item.shirtTypeItemNumber}
                                            onChange={(e) => updateItem(index, 'shirtTypeItemNumber', e.target.value)}
                                            placeholder="T-shirt, Hoodie, Long Sleeve (e.g., Gildan Softstyle 64000)"
                                            helperText="Examples: T-shirt, Hoodie, Long Sleeve; include brand/item # if known"
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} md={2}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Size</InputLabel>
                                            <Select
                                                value={item.size}
                                                label="Size"
                                                onChange={(e) => updateItem(index, 'size', e.target.value)}
                                            >
                                                {sizes.map((size) => (
                                                    <MenuItem key={size} value={size}>
                                                        {size}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                Choose garment size. If you need Tall or Youth, set Size Type below.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                        <FormControl fullWidth>
                                            <InputLabel>Size Type</InputLabel>
                                            <Select
                                                value={item.sizeType}
                                                label="Size Type"
                                                onChange={(e) => updateItem(index, 'sizeType', e.target.value)}
                                            >
                                                <MenuItem value="Regular">Regular</MenuItem>
                                                <MenuItem value="Tall">Tall</MenuItem>
                                                <MenuItem value="Youth">Youth</MenuItem>
                                            </Select>
                                            <FormHelperText>
                                                Please specify if Tall or Youth is required. Verify availability on 
                                                <a href="https://www.sanmar.com/" target="_blank" rel="noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', marginLeft: 4 }}>SanMar</a>.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Shirt Color"
                                            value={item.shirtColor}
                                            onChange={(e) => updateItem(index, 'shirtColor', e.target.value)}
                                            placeholder="Navy, Red, etc."
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Lettering Color</InputLabel>
                                            <Select
                                                value={item.letteringColor}
                                                label="Lettering Color"
                                                onChange={(e) => updateItem(index, 'letteringColor', e.target.value)}
                                            >
                                                <MenuItem value="Black">Black</MenuItem>
                                                <MenuItem value="White">White</MenuItem>
                                            </Select>
                                            <FormHelperText>Other colors may be an additional charge.</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            required
                                            type="number"
                                            label="Quantity"
                                            value={item.quantity}
                                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                            inputProps={{ min: 1, max: 10 }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Notes (Optional)"
                                            value={item.notes}
                                            onChange={(e) => updateItem(index, 'notes', e.target.value)}
                                            placeholder="Special requests..."
                                            inputProps={{ maxLength: 200 }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}
                    
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={addItem}
                        sx={{ mb: 3 }}
                    >
                        Add Another Item
                    </Button>

                    {/* Policy/Notes */}
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                        If your selected color is unavailable in the chosen style, a close color match may be substituted.
                    </Alert>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        If an item number isn’t specified, we’ll use our standard in‑stock adult items:
                        <br />Short Sleeve T‑Shirts: Jerzees 29M
                        <br />Long Sleeve T‑Shirts: Jerzees 29LS
                        <br />Hoodies: Jerzees 996M
                        <br />Full‑Zip Jackets: Jerzees 993M
                    </Alert>

                    <Divider sx={{ my: 3 }} />

                    {/* Actions */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setFormOpen(false);
                                setError('');
                                setSuccess('');
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <ShoppingCart />}
                        >
                            {loading ? 'Submitting...' : `Submit Order (${calculateTotalItems()} items)`}
                        </Button>
                    </Stack>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default ApparelOrderForm; 